import React, { useState, useCallback } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { filesize } from "filesize";
import { IconButton, Button, CircularProgress as MUIProgress, Box } from "@mui/material";
import { useDropzone, DropzoneOptions } from "react-dropzone";

interface IDropZoneProps {
  isLoading: boolean;
  dropZoneOptions: DropzoneOptions;
  handleUploadFiles: (files: File[], clearFiles: () => void) => void;
  allowedFilesDescription: string;
  disabled: boolean;
}

const DropZone: React.FC<IDropZoneProps> = ({
  isLoading,
  dropZoneOptions,
  handleUploadFiles,
  allowedFilesDescription,
  disabled,
}) => {
  const [files, setFiles] = useState<File[]>([]);

  const isDisabled =
    (dropZoneOptions.maxFiles && files.length >= dropZoneOptions.maxFiles) || isLoading || disabled;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const uniqueFiles = acceptedFiles.filter(
        (file) => !files.some((existingFile) => existingFile.name === file.name)
      );
      setFiles((prevFiles) => [...prevFiles, ...uniqueFiles]);
    },
    [files]
  );

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    disabled,
    ...dropZoneOptions,
  });

  const acceptedFileItems = files.map((file: File, index: number) => (
    <Box component="li" key={`${file.name}_${index}`} display="flex" mt="5px">
      {file.name} Size: {filesize(file.size)}{" "}
      <IconButton sx={{ padding: 0 }} disabled={isLoading} onClick={() => handleRemoveFile(index)}>
        <DeleteIcon color="secondary" fontSize="small" />
      </IconButton>
    </Box>
  ));

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.name}>
      {file.name} - {filesize(file.size)}
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  const handleClearFiles = () => {
    setFiles([]);
  };

  return (
    <Box component="section" className="container">
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          borderWidth: "2px",
          borderRadius: "2px",
          borderColor: "gray",
          borderStyle: "dashed",
          backgroundColor: "#fafafa",
          color: "#bdbdbd",
          outline: "none",
          transition: "border .24s ease-in-out",
          ...(isDisabled && { opacity: 0.4 }),
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <p>Drag and drop some files here, or click to select files</p>
        <em>{allowedFilesDescription}</em>
        {dropZoneOptions.maxFiles && (
          <em>
            Maximum allowed amount of uploaded files are {dropZoneOptions.maxFiles}, uploaded{" "}
            {files.length} / {dropZoneOptions.maxFiles}
          </em>
        )}
      </Box>

      {!!acceptedFileItems.length && (
        <aside>
          <h4>Accepted files</h4>
          <ul>{acceptedFileItems}</ul>
        </aside>
      )}

      {!!fileRejectionItems.length && (
        <aside>
          <h4>Rejected files</h4>
          <ul>{fileRejectionItems}</ul>
        </aside>
      )}

      {!!files.length && (
        <Button
          disabled={isLoading}
          onClick={() => handleUploadFiles(files, handleClearFiles)}
          endIcon={isLoading && <MUIProgress color="inherit" size={20} />}
        >
          Upload
        </Button>
      )}
    </Box>
  );
};

export default DropZone;
